import { Injectable } from '@angular/core';
import {
  isAmChartLegendItem,
  isTimelineLegendItem,
  TimelineChart,
  TimelineChartLegendItem,
} from '../models/timeline-chart-input.interface';

@Injectable({
  providedIn: 'root',
})
export class TimelineChartService {
  /**
   * Determines if a legend item is hidden based on the hidden timeline and series keys.
   *
   * @param item - The legend item to check.
   * @param hiddenTimelineKeys - Array of hidden Timeline keys to check against.
   * @param hiddenAmChartSeriesKeys - Array of hidden AmChart Series keys to check against.
   * @returns `true` if the item is hidden; otherwise, `false`.
   */
  isLegendItemHidden(
    item: TimelineChartLegendItem,
    hiddenTimelineKeys: string[],
    hiddenAmChartSeriesKeys: string[]
  ): boolean {
    if (isTimelineLegendItem(item)) {
      return hiddenTimelineKeys.includes(item.timelineKey ?? '');
    }

    if (isAmChartLegendItem(item)) {
      return hiddenAmChartSeriesKeys.includes(item.seriesKey);
    }

    return false;
  }

  /**
   * @returns {Map<string, string>} A map of section keys to color values.
   */
  getLegendKeyColorMap(
    legendItems: TimelineChartLegendItem[]
  ): Map<string, string | undefined> {
    return new Map(
      legendItems
        .filter(isTimelineLegendItem)
        .map(({ sectionKey, color }) => [sectionKey, color])
    );
  }

  /**
   * Returns an array of static time labels for the timeline.
   *
   * Includes the start time, a series of hourly dates between start and end,
   * and the end time itself.
   *
   * Assumes `startTime` and `endTime` are non-null.
   *
   * @returns {Date[]} Array of Date objects representing fixed timeline labels.
   */
  getStaticTimeLabels(start: Date, end: Date, timelineWidth: number): Date[] {
    const hourly = this.getHourlyDates(start, end);
    const filtered = this.filterOverlappingHourlyLabels(
      start,
      end,
      hourly,
      timelineWidth
    );

    return [start, ...filtered, end];
  }

  /**
   * Filters out hourly time labels that would visually overlap
   * with the start or end labels in narrow layouts.
   *
   * This method calculates the pixel distance of each hourly label
   * from the start and end boundaries of the timeline and removes:
   *
   * - The first hourly label if it is too close to the start label.
   * - The last hourly label if it is too close to the end label.
   *
   * The minimum required spacing (in pixels) can be configured via
   * the `minSpacingPx` parameter.
   *
   * This prevents visual collisions between:
   * - Starting Time Label and first Hourly Label
   * - Ending Time Label and last Hourly Label
   *
   * @param start - The starting date/time of the full timeline interval.
   * @param end - The ending date/time of the full timeline interval.
   * @param hourlyDates - Array of hourly Date objects between start and end.
   * @param timelineWidth - The rendered width of the timeline container in pixels.
   * @param minSpacingPx - Minimum required pixel distance from the boundaries
   *                       to keep an hourly label (default: 40px).
   *
   * @returns A filtered array of hourly Date objects that are safe to render
   *          without overlapping the start or end time labels.
   */
  filterOverlappingHourlyLabels(
    start: Date,
    end: Date,
    hourlyDates: Date[],
    timelineWidth: number,
    minSpacingPx = 48
  ): Date[] {
    if (!timelineWidth || !hourlyDates.length) return hourlyDates;

    const duration = this.getDuration(start, end);
    const pxPerMs = timelineWidth / duration;

    return hourlyDates.filter((date, index, arr) => {
      const distanceFromStart = (date.getTime() - start.getTime()) * pxPerMs;

      const distanceFromEnd = (end.getTime() - date.getTime()) * pxPerMs;

      const isFirstHourly = index === 0;
      const isLastHourly = index === arr.length - 1;

      if (isFirstHourly && distanceFromStart < minSpacingPx) {
        return false;
      }

      if (isLastHourly && distanceFromEnd < minSpacingPx) {
        return false;
      }

      return true;
    });
  }

  /**
   * Generates an array of hourly Date objects between the start and end dates.
   *
   * The generated dates start from the next full hour after the start date
   * and go up to the end date (inclusive).
   *
   * Dates within 15 minutes of the start or end are excluded from the result
   * to avoid cluttering labels too close to boundaries.
   *
   * @param {Date} start - The start date/time.
   * @param {Date} end - The end date/time.
   * @returns {Date[]} Array of hourly Date objects between start and end.
   */
  getHourlyDates(start: Date, end: Date): Date[] {
    const dates: Date[] = [];

    // Clone and round up to next full hour after start
    const current = new Date(start);
    current.setMinutes(0, 0, 0);
    current.setHours(current.getHours() + 1);

    while (current <= end) {
      dates.push(new Date(current));
      current.setHours(current.getHours() + 1);
    }

    // Remove first date if it is within 15 minutes of start
    if (dates.length > 1 && this.isLessThan15Minutes(dates[0], start)) {
      dates.shift();
    }

    // Remove last date if it is within 15 minutes of end
    if (
      dates.length > 1 &&
      this.isLessThan15Minutes(end, dates[dates.length - 1])
    ) {
      dates.pop();
    }

    return dates;
  }

  /**
   * Checks whether the absolute difference between two dates is less than 15 minutes.
   *
   * @param date1 - The first date to compare.
   * @param date2 - The second date to compare.
   * @returns True if the difference between the two dates is less than 15 minutes; otherwise, false.
   */
  isLessThan15Minutes(date1: Date, date2: Date): boolean {
    const diffInMilliseconds = Math.abs(date2.getTime() - date1.getTime());
    const diffInMinutes = diffInMilliseconds / (1000 * 60);
    return diffInMinutes < 15;
  }

  /**
   * Returns the interval duration in ms.
   *
   * The result is the absolute difference, so a reversed interval still yields a
   * usable positive span — that inversion is the only case worth reporting, and
   * it is the only case that warns.
   *
   * @param start The starting date of the interval
   * @param end The ending date of the interval
   * @returns {number} The duration in ms; `0` when both are the same instant.
   */
  getDuration(start: Date, end: Date): number {
    if (end.getTime() < start.getTime()) {
      console.warn(
        'Taking the interval duration as an absolute value: the ending date is before the starting date!'
      );
    }

    return Math.abs(start.getTime() - end.getTime());
  }

  /**
   * Calculates the CSS `calc()` string representing the relative position of a time
   * within the total timeline interval, adjusted by an offset in pixels.
   *
   * A zero-length interval (a payload whose start and end are the same instant —
   * a single amChart sample, for example) has no span to divide by: the
   * percentage would come out `NaN` or `Infinity` and the `calc()` string would
   * be rejected by the CSS parser, which drops the declaration silently in a
   * browser and throws in jsdom. In that case the whole interval *is* the start,
   * so every time on it is positioned at 0%.
   *
   * @param time - The target time as a Date object or ISO string.
   * @param offSetInPixels - The pixel offset to subtract from the calculated percentage.
   * @param startTime - The starting date of the timeline
   * @param duration - The duration of the interval in ms
   * @returns A CSS `calc()` string combining the percentage position and pixel offset.
   */
  getRelativePosition(
    time: Date | string,
    offSetInPixels: number,
    startTime: Date,
    duration: number
  ): string {
    if (!duration || !Number.isFinite(duration)) {
      return `calc(0% - ${offSetInPixels}px)`;
    }

    const timeMs =
      time instanceof Date ? time.getTime() : new Date(time).getTime();
    const elapsedMs = timeMs - startTime.getTime();

    const percentage = (elapsedMs / duration) * 100;

    return `calc(${percentage}% - ${offSetInPixels}px)`;
  }

  /**
   * Calculates the horizontal offset for a time label based on its position.
   *
   * - Returns 0 for the first label to avoid extra spacing.
   * - Returns double the offset for the last label for alignment purposes.
   * - Returns the normal offset for any other label.
   *
   * @param offSetInPixels - The base offset value in pixels.
   * @param isFirst - Whether this label is the first in the sequence.
   * @param isLast - Whether this label is the last in the sequence.
   * @returns The calculated offset in pixels.
   */
  getOffSetForTimeLabel(
    offSetInPixels: number,
    isFirst: boolean,
    isLast: boolean
  ): number {
    if (isFirst) {
      return 0;
    }
    if (isLast) {
      return offSetInPixels * 2;
    }
    return offSetInPixels;
  }

  /**
   * Retrieves the earliest section `startTime` across all timelines of a given TimelineChartInputV2.
   *
   * This function looks at every `timelineSection` in each `TimelineChart` contained
   * within the `TimelineChartInputV2`. It collects all defined `startTime` values
   * and returns the earliest one.
   *
   * @param chart - The timeline chart input object to extract the first section startTime from.
   *
   * @returns The earliest `startTime` found as a Date, or `null` if no section
   *          has a defined `startTime`.
   */

  getFirstSectionStartTime(timelineCharts: TimelineChart[]): Date | null {
    const startTimes: string[] = timelineCharts
      .flatMap((t) => t.timelineSections || [])
      .map((s) => s.startTime)
      .filter((s): s is string => !!s);

    if (startTimes.length === 0) {
      return null;
    }

    return new Date(
      startTimes.reduce((earliest, current) =>
        current < earliest ? current : earliest
      )
    );
  }
  /**
   * Retrieves the latest section `endTime` across all timelines of a given TimelineChartInputV2.
   *
   * This function looks at every `timelineSection` in each `TimelineChart` contained
   * within the `TimelineChartInputV2`. It collects all defined `endTime` values
   * and returns the latest one.
   *
   * @param chart - The timeline chart input object to extract the last section endTime from.
   *
   * @returns The latest `endTime` found as a Date, or `null` if no section
   *          has a defined `endTime`.
   */
  getLastSectionEndTime(timelineCharts: TimelineChart[]): Date | null {
    const endTimes: string[] = timelineCharts
      .flatMap((t) => t.timelineSections || [])
      .map((s) => s.endTime)
      .filter((s): s is string => !!s);

    if (endTimes.length === 0) {
      return null;
    }

    return new Date(
      endTimes.reduce((latest, current) =>
        current > latest ? current : latest
      )
    );
  }
}
